import type {
  DerivedContext,
  CreateDerivedContextBody,
  UpdateDerivedContextBody,
} from '@lib/api-types/derived-contexts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, unwrap } from '../api/client.ts';

/**
 * View-model hooks for the derivedContexts resource. Mirror of
 * view-models/business-functions.ts and view-models/engineers.ts.
 */

export interface ListDerivedContextsOptions {
  atEffective?: number;
  atAuthored?: number;
  branchId?: number;
}

const KEYS = {
  all: (slug: string) => ['derivedContexts', slug] as const,
  list: (slug: string, opts: ListDerivedContextsOptions) =>
    ['derivedContexts', slug, 'list', opts] as const,
};

function listQuery(opts: ListDerivedContextsOptions) {
  const q: Record<string, string> = {};
  if (opts.atEffective !== undefined) q.at_effective = String(opts.atEffective);
  if (opts.atAuthored !== undefined) q.at_authored = String(opts.atAuthored);
  if (opts.branchId !== undefined) q.branch_id = String(opts.branchId);
  return q;
}

interface ListResponseBody {
  object: 'list';
  url: string;
  has_more: boolean;
  data: DerivedContext[];
}

interface SingleResponseBody {
  derivedContext: DerivedContext;
}

interface DeleteResponseBody {
  deleted: true;
  id: string;
}

export function useDerivedContexts(
  orgSlug: string | null,
  opts: ListDerivedContextsOptions = {},
) {
  return useQuery({
    queryKey: orgSlug
      ? KEYS.list(orgSlug, opts)
      : ['derivedContexts', null, 'list'],
    queryFn: async () => {
      if (!orgSlug) throw new Error('useDerivedContexts called without orgSlug');
      const res = await apiClient.v1.orgs[':slug'].derived_contexts.$get({
        param: { slug: orgSlug },
        query: listQuery(opts),
      });
      const body = await unwrap<ListResponseBody>(res);
      return body.data;
    },
    enabled: !!orgSlug,
    staleTime: 30_000,
  });
}

export function useDerivedContext(orgSlug: string | null, id: string | null) {
  return useQuery({
    queryKey: orgSlug && id
      ? (['derivedContexts', orgSlug, 'one', id] as const)
      : (['derivedContexts', null, 'one'] as const),
    queryFn: async () => {
      if (!orgSlug || !id) throw new Error('useDerivedContext called without orgSlug+id');
      const res = await apiClient.v1.orgs[':slug'].derived_contexts[':id'].$get({
        param: { slug: orgSlug, id },
        query: {},
      });
      const body = await unwrap<SingleResponseBody>(res);
      return body.derivedContext;
    },
    enabled: !!orgSlug && !!id,
    staleTime: 30_000,
  });
}

export function useCreateDerivedContext(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDerivedContextBody) => {
      const res = await apiClient.v1.orgs[':slug'].derived_contexts.$post({
        param: { slug: orgSlug },
        json: body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.derivedContext;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useUpdateDerivedContext(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: UpdateDerivedContextBody }) => {
      const res = await apiClient.v1.orgs[':slug'].derived_contexts[':id'].$patch({
        param: { slug: orgSlug, id: args.id },
        json: args.body,
      });
      const data = await unwrap<SingleResponseBody>(res);
      return data.derivedContext;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}

export function useDeleteDerivedContext(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.v1.orgs[':slug'].derived_contexts[':id'].$delete({
        param: { slug: orgSlug, id },
      });
      return unwrap<DeleteResponseBody>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.all(orgSlug) });
    },
  });
}
